import ApiCollection from '@/classes/api/ApiCollection'
import users from '@/api/modules/users'
import warnings from '@/api/modules/warnings'
import watches from '@/api/modules/watches'
import profile from '@/api/modules/profile'
import files from '@/api/modules/files'
import log from '@/utils/consolelog'
import objIsEmpty from '@/utils/objIsEmpty'
import generateBase64Picture from '@/utils/generateBase64Picture'
import * as mutations from './mutations'

const api = {
	users,
	warnings,
	watches,
	profile,
	files
}

const accountsInitialState = {
	data: [],
	loading: false,
	error: null
}

const alertsInitialState = {
	data: [],
	loading: false,
	error: null
}

const watchesInitialState = {
	data: [],
	loading: false,
	error: null
}

const profileInitialState = {
	data: {},
	loading: false,
	error: null
}

const pictureInitialState = {
	data: null,
	loading: false,
	error: null
}

class Account {
	constructor (account) {
		this.uid = account.uid
		this.name = account.fullname
		this.picture_uid = account.picture
		this.picture = Object.assign({}, pictureInitialState)
		this.alerts = Object.assign({}, alertsInitialState)
		this.watches = Object.assign({}, watchesInitialState)
	}
}

class Alert {
	constructor (alert) {
		Object.assign(this, alert)
	}
}

class Watch {
	constructor (watch) {
		Object.assign(this, watch)
		this.profile = Object.assign({}, profileInitialState)
		this.picture_uid = this.picture
		this.picture = Object.assign({}, pictureInitialState)
	}
}

class Profile {
	constructor (profile) {
		Object.assign(this, profile)
	}
}

export default {
	state: Object.assign({}, accountsInitialState),
	getters: {
		getAccount: state => uid => {
			return state.data.find(account => account.uid == uid)
		},
		getAccountWatches: state => uid => {
			const account = state.data.find(account => account.uid == uid)
			return account ? account.watches : new ApiCollection()
		},
		getAccountWatch: state => (uid, watch_id) => {
			const account = state.data.find(account => account.uid == uid)
			return account ? account.watches.data.find(watch => watch.id == watch_id) : null
		},
		getAccountAlertsUnread: state => uid => {
			const account = state.data.find(account => account.uid == uid)
			return account ? {
				data: account.alerts.data.filter(alert => alert.type === 1 && !alert.reading_time),
				loading: account.alerts.loading,
				error: account.alerts.error
			} : new ApiCollection()
		}
	},
	mutations: {
		// Accounts
		[mutations.SET_ACCOUNTS] (state, value) {
			state.data = value
		},
		[mutations.SET_ACCOUNTS_LOADING] (state, value) {
			state.loading = value
		},
		[mutations.SET_ACCOUNTS_ERROR] (state, value) {
			state.error = value
		},
		[mutations.CLEAR_ACCOUNTS] (state) {
			Object.assign(state, accountsInitialState)
		},
		// Pictures
		[mutations.SET_ACCOUNT_PICTURE] (state, { user_uid, picture }) {
			const account = state.data.find(account => account.uid == user_uid)
			if (account) account.picture.data = picture
		},
		[mutations.SET_ACCOUNT_PICTURE_LOADING] (state, { user_uid, loading }) {
			const account = state.data.find(account => account.uid == user_uid)
			if (account) account.picture.loading = loading
		},
		[mutations.SET_ACCOUNT_PICTURE_ERROR] (state, { user_uid, error }) {
			const account = state.data.find(account => account.uid == user_uid)
			if (account) account.picture.error = error
		},
		// Alerts
		[mutations.SET_ACCOUNT_ALERTS] (state, { user_uid, alerts }) {
			const account = state.data.find(account => account.uid == user_uid)
			if (account) account.alerts.data = alerts
		},
		[mutations.SET_ACCOUNT_ALERTS_LOADING] (state, { user_uid, loading }) {
			const account = state.data.find(account => account.uid == user_uid)
			if (account) account.alerts.loading = loading
		},
		[mutations.SET_ACCOUNT_ALERTS_ERROR] (state, { user_uid, error }) {
			const account = state.data.find(account => account.uid == user_uid)
			if (account) account.alerts.error = error
		},
		// Watches
		[mutations.SET_ACCOUNT_WATCHES] (state, { user_uid, watches }) {
			const account = state.data.find(account => account.uid == user_uid)
			if (account) account.watches.data = watches
		},
		[mutations.SET_ACCOUNT_WATCHES_LOADING] (state, { user_uid, loading }) {
			const account = state.data.find(account => account.uid == user_uid)
			if (account) account.watches.loading = loading
		},
		[mutations.SET_ACCOUNT_WATCHES_ERROR] (state, { user_uid, error }) {
			const account = state.data.find(account => account.uid == user_uid)
			if (account) account.watches.error = error
		},
		// Profiles
		[mutations.SET_ACCOUNT_WATCH_PROFILE] (state, { user_uid, watch_id, profile }) {
			const account = state.data.find(account => account.uid == user_uid)
			if (account) {
				const watch = account.watches.data.find(watch => watch.id == watch_id)
				if (watch) watch.profile.data = profile
			}
		},
		[mutations.SET_ACCOUNT_WATCH_PROFILE_LOADING] (state, { user_uid, watch_id, loading }) {
			const account = state.data.find(account => account.uid == user_uid)
			if (account) {
				const watch = account.watches.data.find(watch => watch.id == watch_id)
				if (watch) watch.profile.loading = loading
			}
		},
		[mutations.SET_ACCOUNT_WATCH_PROFILE_ERROR] (state, { user_uid, watch_id, error }) {
			const account = state.data.find(account => account.uid == user_uid)
			if (account) {
				const watch = account.watches.data.find(watch => watch.id == watch_id)
				if (watch) watch.profile.error = error
			}
		},
		// Pictures
		[mutations.SET_ACCOUNT_WATCH_PICTURE] (state, { user_uid, watch_id, picture }) {
			const account = state.data.find(account => account.uid == user_uid)
			if (account) {
				const watch = account.watches.data.find(watch => watch.id == watch_id)
				if (watch) watch.picture.data = picture
			}
		},
		[mutations.SET_ACCOUNT_WATCH_PICTURE_LOADING] (state, { user_uid, watch_id, loading }) {
			const account = state.data.find(account => account.uid == user_uid)
			if (account) {
				const watch = account.watches.data.find(watch => watch.id == watch_id)
				if (watch) watch.picture.loading = loading
			}
		},
		[mutations.SET_ACCOUNT_WATCH_PICTURE_ERROR] (state, { user_uid, watch_id, error }) {
			const account = state.data.find(account => account.uid == user_uid)
			if (account) {
				const watch = account.watches.data.find(watch => watch.id == watch_id)
				if (watch) watch.picture.error = error
			}
		}
	},
	actions: {
		getAccounts ({ commit, state, dispatch }, { context, user_uid }) {
			const getUser = () => {
				return api.users.get(context)
					.then(response => response.body)
			}
			const getPermittedUsers = () => {
				return api.users.getPermittedUsers(context, user_uid)
					.then(response => response.body)
			}
			commit(mutations.SET_ACCOUNTS_LOADING, true)
			commit(mutations.SET_ACCOUNTS_ERROR, null)
			Promise.all([ getUser(), getPermittedUsers() ])
				.then(([ user, permittedUsers ]) => {
					log.log('store', 'getAccounts() OK', [ user, permittedUsers ])()
					const accounts = []
					accounts.push(new Account(user))
					permittedUsers.forEach(user => {
						accounts.push(new Account(user))
					})
					commit(mutations.SET_ACCOUNTS, accounts)
				})
				.catch(error => {
					log.error('store', 'getAccounts() ERROR', error)()
					commit(mutations.SET_ACCOUNTS_ERROR, error)
				})
				.finally(() => {
					commit(mutations.SET_ACCOUNTS_LOADING, false)
					state.data.forEach(account => {
						dispatch('getAccountAlerts', { context, user_uid: account.uid })
						dispatch('getAccountWatches', { context, user_uid: account.uid })
						if (account.picture_uid) dispatch('getAccountPicture', { context, user_uid, file_uid: account.picture_uid })
					})
				})
		},
		getAccountPicture ({ commit, state, dispatch }, { context, user_uid, file_uid }) {
			const getAccountPictureData = () => {
				return api.files.get(context, user_uid, null, file_uid)
					.then(response => {
						return response.body
					})
			}
			const getAccountPictureMetadata = () => {
				return api.files.getMetadata(context, user_uid, null, file_uid)
					.then(response => {
						return response.body
					})
			}
			commit(mutations.SET_ACCOUNT_PICTURE_LOADING, { user_uid, loading: true })
			commit(mutations.SET_ACCOUNT_PICTURE_ERROR, { user_uid, error: null })
			Promise.all([ getAccountPictureData(), getAccountPictureMetadata() ])
				.then(([ pictureDataResult, pictureMetadataResult ]) => {
					log.log('store', 'getAccountPicture() OK', [ pictureDataResult, pictureMetadataResult ])()
					const picture = generateBase64Picture(pictureDataResult, pictureMetadataResult.extension)
					commit(mutations.SET_ACCOUNT_PICTURE, { user_uid, picture })
				})
				.catch(error => {
					log.error('store', 'getAccountPicture() ERROR', error)()
					commit(mutations.SET_ACCOUNT_PICTURE_ERROR, { user_uid, error })
				})
				.finally(() => {
					commit(mutations.SET_ACCOUNT_PICTURE_LOADING, { user_uid, loading: false })
				})
		},
		getAccountAlerts ({ commit, state, dispatch }, { context, user_uid }) {
			const getAlerts = () => {
				return api.warnings.getAll(context, user_uid)
					.then(response => response.body)
			}
			commit(mutations.SET_ACCOUNT_ALERTS_LOADING, { user_uid, loading: true })
			commit(mutations.SET_ACCOUNT_ALERTS_ERROR, { user_uid, error: null })
			Promise.all([ getAlerts() ])
				.then(([ alertsResult ]) => {
					log.log('store', 'getAccountAlerts() OK', [ alertsResult ])()
					const alerts = []
					alertsResult.forEach(alert => {
						alerts.push(new Alert(alert))
					})
					commit(mutations.SET_ACCOUNT_ALERTS, { user_uid, alerts })
				})
				.catch(error => {
					log.error('store', 'getAccountAlerts() ERROR', error)()
					commit(mutations.SET_ACCOUNT_ALERTS_ERROR, { user_uid, error })
				})
				.finally(() => {
					commit(mutations.SET_ACCOUNT_ALERTS_LOADING, { user_uid, loading: false })
				})
		},
		getAccountWatches ({ commit, state, dispatch }, { context, user_uid }) {
			const getWatches = () => {
				return api.watches.getAll(context, user_uid)
					.then(response => response.body.sort((a, b) => a.name.localeCompare(b.name)))
			}
			commit(mutations.SET_ACCOUNT_WATCHES_LOADING, { user_uid, loading: true })
			commit(mutations.SET_ACCOUNT_WATCHES_ERROR, { user_uid, error: null })
			Promise.all([ getWatches() ])
				.then(([ watchesResult ]) => {
					log.log('store', 'getAccountWatches() OK', [ watchesResult ])()
					const watches = []
					watchesResult.forEach(watch => {
						watches.push(new Watch(watch))
					})
					commit(mutations.SET_ACCOUNT_WATCHES, { user_uid, watches })
				})
				.catch(error => {
					log.error('store', 'getAccountWatches() ERROR', error)()
					commit(mutations.SET_ACCOUNT_WATCHES_ERROR, { user_uid, error })
				})
				.finally(() => {
					commit(mutations.SET_ACCOUNT_WATCHES_LOADING, { user_uid, loading: false })
					state.data.forEach(account => {
						if (account.uid == user_uid) {
							account.watches.data.forEach(watch => {
								dispatch('getAccountWatchProfile', { context, user_uid, watch_id: watch.id })
								if (watch.picture_uid) dispatch('getAccountWatchPicture', { context, user_uid, watch_id: watch.id, file_uid: watch.picture_uid })
							})
						}
					})
				})
		},
		getAccountWatchProfile ({ commit, state, dispatch }, { context, user_uid, watch_id }) {
			const getWatchProfile = () => {
				return api.profile.get(context, user_uid, watch_id)
					.then(response => {
						return response.body
					})
			}
			commit(mutations.SET_ACCOUNT_WATCH_PROFILE_LOADING, { user_uid, watch_id, loading: true })
			commit(mutations.SET_ACCOUNT_WATCH_PROFILE_ERROR, { user_uid, watch_id, error: null })
			Promise.all([ getWatchProfile() ])
				.then(([ profileResult ]) => {
					log.log('store', 'getAccountWatchProfile() OK', [ profileResult ])()
					const profile = new Profile(profileResult)
					commit(mutations.SET_ACCOUNT_WATCH_PROFILE, { user_uid, watch_id, profile })
				})
				.catch(error => {
					log.error('store', 'getAccountWatchProfile() ERROR', error)()
					commit(mutations.SET_ACCOUNT_WATCH_PROFILE_ERROR, { user_uid, watch_id, error })
				})
				.finally(() => {
					commit(mutations.SET_ACCOUNT_WATCH_PROFILE_LOADING, { user_uid, watch_id, loading: false })
				})
		},
		getAccountWatchPicture ({ commit, state, dispatch }, { context, user_uid, watch_id, file_uid }) {
			const getWatchPictureData = () => {
				return api.files.get(context, user_uid, null, file_uid)
					.then(response => {
						return response.body
					})
			}
			const getWatchPictureMetadata = () => {
				return api.files.getMetadata(context, user_uid, null, file_uid)
					.then(response => {
						return response.body
					})
			}
			commit(mutations.SET_ACCOUNT_WATCH_PICTURE_LOADING, { user_uid, watch_id, loading: true })
			commit(mutations.SET_ACCOUNT_WATCH_PICTURE_ERROR, { user_uid, watch_id, error: null })
			Promise.all([ getWatchPictureData(), getWatchPictureMetadata() ])
				.then(([ pictureDataResult, pictureMetadataResult ]) => {
					log.log('store', 'getAccountWatchPicture() OK', [ pictureDataResult, pictureMetadataResult ])()
					const picture = generateBase64Picture(pictureDataResult, pictureMetadataResult.extension)
					commit(mutations.SET_ACCOUNT_WATCH_PICTURE, { user_uid, watch_id, picture })
				})
				.catch(error => {
					log.error('store', 'getAccountWatchPicture() ERROR', error)()
					commit(mutations.SET_ACCOUNT_WATCH_PICTURE_ERROR, { user_uid, watch_id, error })
				})
				.finally(() => {
					commit(mutations.SET_ACCOUNT_WATCH_PICTURE_LOADING, { user_uid, watch_id, loading: false })
				})
		}
	}
}



// WEBPACK FOOTER //
// ./src/store/modules/accounts.js